import React, { useState, useEffect } from 'react';
import { LuX, LuCirclePlus, LuCircleCheck, LuLoader } from 'react-icons/lu';
import apiClient from '../api/apiClient';

const AddBrokerModal = ({ onClose, onBrokerAdded }) => {
  const [availableBrokers, setAvailableBrokers] = useState([]);
  const [selectedBroker, setSelectedBroker] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingBrokers, setLoadingBrokers] = useState(true);

  useEffect(() => {
    const fetchMasterBrokers = async () => {
      try {
        const response = await apiClient.get('/master-brokers');
        setAvailableBrokers(response.data);
        if (response.data.length > 0) {
          setSelectedBroker(response.data[0].name);
        }
      } catch (error) {
        console.error('Error fetching master brokers:', error);
        setError('Failed to load available brokers');
      } finally {
        setLoadingBrokers(false);
      }
    };

    fetchMasterBrokers();
  }, []);

  const handleConnect = async () => {
    if (!selectedBroker || !apiKey || !apiSecret) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/brokers', {
        name: selectedBroker,
        api_key: apiKey,
        api_secret: apiSecret,
      });

      // Call the callback to refresh the parent component
      if (onBrokerAdded) {
        onBrokerAdded();
      }

      onClose();
    } catch (error) {
      console.error('Error connecting broker:', error);
      setError(error.response?.data?.message || 'Failed to connect broker. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-3">
          <LuCirclePlus className="text-blue-600" size={28} />
          <h2 className="text-2xl font-bold text-slate-800">Add Broker</h2>
        </div>
        <button onClick={onClose} className="p-1 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors">
          <LuX size={24} />
        </button>
      </div>

      <div className="space-y-5">
        {loadingBrokers && <p className="text-center text-gray-600">Loading available brokers...</p>}
        {!loadingBrokers && (
          <div>
            <label className="text-sm font-semibold text-slate-600 mb-2 block">Select Broker</label>
            <div className="grid grid-cols-2 gap-4">
              {availableBrokers.map((broker) => (
                <button
                  key={broker.id}
                  onClick={() => setSelectedBroker(broker.name)}
                  className={`relative p-4 border-2 rounded-xl text-center space-y-3 transition-all duration-200 ${selectedBroker === broker.name
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-400'
                    }`}
                >
                  <p className="text-sm font-semibold text-slate-800">{broker.display_name}</p>

                  {selectedBroker === broker.name && (
                    <div className="absolute top-2 right-2 p-0.5 bg-white rounded-full">
                      <LuCircleCheck className="text-blue-600" size={20} />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="text-sm font-semibold text-slate-600 mb-2 block">API Key *</label>
          <input
            type="text"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-colors"
            placeholder="Enter your API key"
          />
        </div>

        <div>
          <label className="text-sm font-semibold text-slate-600 mb-2 block">API Secret *</label>
          <input
            type="password"
            value={apiSecret}
            onChange={(e) => setApiSecret(e.target.value)}
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-colors"
            placeholder="Enter your API secret"
          />
        </div>

        {error && <p className="text-red-600 text-sm">{error}</p>}

        <div className="flex items-center justify-end space-x-3 pt-4">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-semibold text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleConnect}
            disabled={loading || loadingBrokers}
            className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading && <LuLoader className="animate-spin" size={16} />}
            <span>{loading ? 'Connecting...' : 'Connect'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddBrokerModal;
